<template>
  <section class="history-data">
    <div id="print-ignore" class="head">
      <div class="form-item">
        <label for="start-date">{{ $t("creditHistory.type") }}</label>
        <select
          v-model="typeFilter"
          v-on:change="filterData"
        >
          <option default value="All">{{ $t("creditHistory.select.default") }}</option>
          <option v-for="(type, key) in types" v-bind:value="type">
            {{ type }}
          </option>
        </select>
      </div>
      <div class="form-item">
        <label for="start-date">From</label>
        <date-picker
          v-model="startTime"
          valueType="timestamp"
          :input-attr="{name: 'start-date'}"
          v-on:change="dateCheck"
        />
      </div>
      <div class="form-item">
        <label for="end-date">To</label>
        <date-picker
          v-model="endTime"
          valueType="timestamp"
          :input-attr="{name: 'end-date'}"
          v-on:change="dateCheck"
        />
      </div>
      <a class="button pdf" v-on:click.prevent="print" href="#">{{ $t("creditHistory.button.printPDF") }}<span class="icn"/></a>
    </div>
    <table v-if="!dateErr" id="credit-data" class="datatable">
      <caption class="caption">Credit history table</caption>
      <tr class="header">
        <th scope="col">{{ $t("creditHistory.table.date") }}</th>
        <th scope="col">{{ $t("creditHistory.table.time") }}</th>
        <th scope="col">Project</th>
        <th scope="col">{{ $t("creditHistory.table.user") }}</th>
        <th scope="col">{{ $t("creditHistory.table.reason") }}</th>
        <th scope="col" style="text-align: center;">{{ $t("creditHistory.table.type") }}</th>
        <th scope="col" style="text-align: center;">{{ $t("creditHistory.table.credits") }}</th>
      </tr>
      <tr v-for="(me, index) in tableData" v-bind:key="index">
        <td>{{ me.eventTimestamp | shortDate }}</td>
        <td>{{ me.eventTimestamp | time }}</td>
        <td>
          <router-link v-bind:to="'/project/' + projectDetails(me).id">
            {{ projectDetails(me).name }}
          </router-link>
        </td>
        <td>{{ me.userName }}</td>
        <td>{{ me.reason | trimReason }}</td>
        <td style="text-align: center;">{{ me.type }}</td>
        <td style="text-align: center;">{{ me.modification }}</td>
      </tr>
    </table>
    <h2 v-if="dateErr" class="error">{{ $t("creditHistory.errorMsg") }}</h2>
  </section>
</template>

<script>
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import print from 'print-js'

import { planService } from '@/services/plan.service'

export default {
  name: 'CreditHistory',
  props: [ 'historyData' ],
  components: { DatePicker },
  data: function() {
    return {
      listing: [],
      tableData: [],
      types: [],
      typeFilter: 'All',
      // Initial start time 1 week ago.
      startTime: Date.now() - 604800000,
      endTime: Date.now(),
      dateErr: false,
      typeLang: {
        'Allocation': this.$t("creditHistory.select.item01"),
        'Consumption': this.$t("creditHistory.select.item02"),
        'Creation': this.$t("creditHistory.select.item03"),
        'Manual Modification': this.$t("creditHistory.select.item04"),
        'Refund': this.$t("creditHistory.select.item05"),
        'Transfer': this.$t("creditHistory.select.item06"),
        'Update': this.$t("creditHistory.select.item07")
      }

    };
  },
  created: function() {
    this.listing = this.historyData
    this.projectDetails(this.listing[0])
    this.filterData()
    this.gatherTypes()
  },
  methods: {
    projectDetails: function (dataItem) {
      let project= {}
      for(let t=0; t<dataItem.tags.length; t++) {
        switch(dataItem.tags[t].key) {
          case 'projectId':
            project.id = dataItem.tags[t].value
            break
          case 'projectName':
            project.name = dataItem.tags[t].value
            break
        }
      }
      return project
    },
    print: function () {
      let printStyles = `
        @page {
          margin: 1cm 1cm 2cm;
        }
        @media print {
          * { font-family: Sans; }
        }
        @media print {
          table {
            border-spacing: 0;
            border-collapse: collapse;
            margin-top: 27px;
          }
        }
        @media print {
          tr.header {
            background: #f3f3f3;
            text-align: left;
            text-transform: uppercase;
          }
        }
        @media print {
          th,td {
            padding: 13px 10px;
            border-bottom: 2px solid #b8b8b8;
            font-size: 11px;
          }
        }
        @media print {
          h1,h2,h3,h4 { margin: 0; }
        }
        @media print {
          h1 {
            font-size: 48px;
            font-weight: normal;
            font-family: "Work Sans" Sans;
          }
        }
        @media print {
          h2 {
            font-weight: normal;
          }
        }
        @media print {
          h2.report-topic {
            margin-top: 50px;
          }
        @media print {
          h2.report-subhead {
            font-size: 20px;
            line-height: 30px;
            margin: 0;
          }
        }
        @media print {
          strong {
            font-weight: bold;
          }
        }
        @media print {
          img.print-logo {
            margin-bottom: 82px;
          }
        }
`
      print({
        printable: 'printable',
        type: 'html',
        ignoreElements: ['print-ignore'],
        style: printStyles,
        scanStyles: false,
        onError: err => console.error(err)
      })
    },
    dateCheck: function () {
      this.dateErr = false
      if(this.startTime > this.endTime) {
        this.dateErr = true
      }
      else {
        this.filterData()
      }
    },
    gatherTypes: function () {
      this.types = []

      for(let l=0; l<this.listing.length; l++) {
        if(this.listing[l].type
            && !this.types.includes(this.typeLang[this.listing[l].type])) {
          this.types.push(this.typeLang[this.listing[l].type])
        }
      }
    },
    filterData: function () {
      this.tableData = []

      let startCheck = moment(this.startTime, 'x');
      let endCheck = moment(this.endTime, 'x');

      for(let l=0; l<this.listing.length; l++) {
        if(moment(this.listing[l].eventTimestamp, 'x')
              .isSameOrAfter(startCheck, 'day')
            && moment(this.listing[l].eventTimestamp, 'x')
              .isSameOrBefore(endCheck, 'day')) {

          if(this.typeFilter === 'All'
              || this.typeLang[this.listing[l].type] === this.typeFilter) {
            let tmpListing = Object.assign({}, this.listing[l]);
            tmpListing.type = this.typeLang[this.listing[l].type]
            this.tableData.push(tmpListing)
          }
        }
        if(moment(this.listing[l].eventTimestamp, 'x')
            .isBefore(startCheck, 'day')) {
          break
        }
      }

    }
  },
  filters: {
    shortDate: function (date) {
      return moment(date).format('MMM D, YYYY')
    },
    time: function (date) {
      return moment(date).format('h:mm:ss a')
    },
    trimReason: function(rawReason) {
      let parts = rawReason.split(',')
      if(parts[0] === 'Consume a graph credit') {
        return 'Create Graph (' + parts[2].slice(9) + ')'
      }
      return rawReason
    }
  }
}

</script>
<style lang="scss">
@import '~vue2-datepicker/index.css';

button.mx-btn {
  height: auto !important;
  min-width: 0 !important;
}

section.history-data {
  padding: 20px;
  margin: 20px 0 150px;
  background: $surface-alpha;
  border-radius: 15px;
  max-width: 1186px;

  a.button {
    margin: 0 0 25px 0;
    height: 50px;
    line-height: 50px;
    background: $secondary url('../assets/icons/pdf.png') no-repeat 155px center;
  }
}

.icn {
  display: inline-block;
  width: 21px;
  height: 20px;
}

.head {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
}

.form-item {
  margin-right: 21px;
}

label {
  display: block;
}

table {
  width: 100%;
  margin-top: 0;
  border-collapse: collapse;
  .caption {
    display: none;
  }
}

tr.header {
  color: $text-light;
  background: $primary;
  th {
    text-align: left;
    border: none;
    padding: 7px 0 7px 28px;
  }
}

td {
  text-align: left;
  background: $surface-beta;
  border-bottom: 2px solid $surface-alpha;
  padding: 21px 0 21px 28px;
}
</style>
